import { useId, type Ref } from 'react';
import { TEXT } from '@/components/typography';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { formatMoney } from './money';

/**
 * A whole amount in đồng (finance plan Task 14), with `DateField`'s furniture: a visible `Label`, the input, a muted
 * "₫" after it, then the hint and the error, both tied to the input. The value is the digits the API takes ('' for
 * none; a leading '-' only when `signed`); the input shows them grouped ("2.500.000.000"). Nothing is parsed into a
 * number, so every one of the 38 digits the column holds survives the typing.
 */

/** Digits only, no leading zero, at most 38; a '-' in front when signed and the typed text starts with - or −. */
export function amountOf(typed: string, signed = false): string {
  const digits = typed.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 38);
  return signed && /^\s*[-−]/.test(typed) ? `-${digits}` : digits;
}

/** The value as the input shows it: grouped, a lone '-' as typed. */
const shown = (value: string): string => (value === '' || value === '-' ? value : formatMoney(value));

export function AmountField({
  id: given, label, value, onChange, placeholder, hint, error, required, readOnly, signed = false, inputRef,
}: {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  readOnly?: boolean;
  signed?: boolean;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const own = useId();
  const id = given ?? own;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          ref={inputRef} id={id} inputMode="numeric" autoComplete="off" value={shown(value)} placeholder={placeholder}
          required={required} readOnly={readOnly}
          className="pr-8 tabular-nums read-only:text-[hsl(var(--h-muted-foreground))]"
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(amountOf(event.target.value, signed))}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-[hsl(var(--h-muted-foreground))]"
        >
          ₫
        </span>
      </div>
      {hint && <p className={TEXT.hint} id={hintId}>{hint}</p>}
      {error && <p className={cn(TEXT.fieldError)} id={errorId} role="alert">{error}</p>}
    </div>
  );
}
